import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CreditCard,
  FileText,
  ZoomIn,
  Car,
  Star,
  Activity,
  ArrowLeft,
  CheckCircle,
  XCircle,
  ShieldOff,
  ShieldCheck,
  TrendingUp,
  Package,
  Wallet,
  Building2,
  BadgeCheck,
  Clock,
  AlertCircle,
  Trash2,
  MessageSquare,
  Radio,
} from "lucide-react";
import { DriverBankAccount } from "@/types/api.types";
import { formatDate, formatCurrency } from "@/lib/utils";
import { vehicleCapacityLabel } from "@/constants/vehicles";
import { ROUTES } from "@/constants/routes";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DriverLocationMap } from "../components/DriverLocationMap";
import {
  useDriver,
  useApproveDriver,
  useRejectDriver,
  useSuspendDriver,
  useActivateDriver,
  useResetDriverData,
} from "../hooks/useDrivers";

function VerificationBadge({ status }: { status?: string }) {
  if (!status) return null;
  const map: Record<string, "success" | "destructive" | "warning"> = {
    approved: "success",
    rejected: "destructive",
    pending: "warning",
  };
  return (
    <Badge variant={map[status] ?? "secondary"} className="capitalize">
      {status}
    </Badge>
  );
}

function AvailabilityBadge({ status }: { status?: string }) {
  return (
    <Badge variant={status === "online" ? "success" : "secondary"} className="capitalize">
      {status || "offline"}
    </Badge>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color = "primary",
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card">
      <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`h-5 w-5 text-${color}`} />
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
        <p className="text-xl font-bold mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-border/40 last:border-0">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold mt-0.5 break-words">{value ?? "—"}</p>
      </div>
    </div>
  );
}

function DocumentCard({
  title,
  imageUrl,
  onPreview,
}: {
  title: string;
  imageUrl?: string;
  onPreview: () => void;
}) {
  if (!imageUrl) {
    return (
      <div className="rounded-xl overflow-hidden border border-border bg-card">
        <div className="h-44 bg-muted flex flex-col items-center justify-center gap-2">
          <FileText className="h-8 w-8 text-muted-foreground/40" />
          <span className="text-xs text-muted-foreground">Not Uploaded</span>
        </div>
        <div className="p-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="group rounded-xl overflow-hidden border border-border bg-card hover:shadow-md transition-all duration-200">
      <div className="relative h-44 bg-muted cursor-pointer overflow-hidden" onClick={onPreview}>
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              `https://placehold.co/400x250/1e293b/94a3b8?text=${encodeURIComponent(title)}`;
          }}
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="flex items-center gap-1.5 text-white text-sm font-medium bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
            <ZoomIn className="h-4 w-4" />
            Preview
          </div>
        </div>
      </div>
      <div className="p-3 flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold">{title}</p>
      </div>
    </div>
  );
}

function RazorpayBadge({ account }: { account: DriverBankAccount }) {
  if (account.razorpay_sync_status === "synced" && account.razorpay_fund_account_id) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <BadgeCheck className="h-3 w-3" />
        RazorpayX Ready
      </span>
    );
  }
  if (account.razorpay_sync_status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
        <AlertCircle className="h-3 w-3" />
        Sync Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
      <Clock className="h-3 w-3" />
      Sync Pending
    </span>
  );
}

function BankAccountCard({ account }: { account: DriverBankAccount }) {
  const masked = `••••••${account.account_number.slice(-4)}`;
  return (
    <div className={`relative rounded-xl border p-4 flex flex-col gap-3 bg-card transition-shadow hover:shadow-md ${account.is_primary ? "border-primary/40 bg-primary/5" : "border-border"}`}>
      {/* Primary badge */}
      {account.is_primary && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-primary text-primary-foreground">
          <CheckCircle className="h-3 w-3" />
          Primary
        </span>
      )}

      {/* Bank icon + name */}
      <div className="flex items-center gap-3 pr-16">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Building2 className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold truncate">{account.bank_name}</p>
          <p className="text-xs text-muted-foreground truncate">{account.branch}</p>
        </div>
      </div>

      {/* Account details grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="col-span-2">
          <p className="text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Account Holder</p>
          <p className="font-semibold">{account.account_holder_name}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Account No.</p>
          <p className="font-mono font-semibold">{masked}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider font-medium mb-0.5">IFSC</p>
          <p className="font-mono font-semibold">{account.ifsc_code}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Type</p>
          <p className="capitalize font-semibold">{account.account_type}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Verified</p>
          <p className={`font-semibold ${account.is_verified ? "text-emerald-600" : "text-amber-600"}`}>
            {account.is_verified ? "Yes" : "Pending"}
          </p>
        </div>
      </div>

      {/* RazorpayX sync status */}
      <div className="pt-1 border-t border-border/50">
        <RazorpayBadge account={account} />
      </div>
    </div>
  );
}

function formatLastActive(dateStr?: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export function DriverDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [suspendModal, setSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [resetModal, setResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");

  const { data: driver, isLoading, isError } = useDriver(id!);
  const approveMutation = useApproveDriver();
  const rejectMutation = useRejectDriver();
  const suspendMutation = useSuspendDriver();
  const activateMutation = useActivateDriver();
  const resetMutation = useResetDriverData();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !driver) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <XCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Driver not found</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This driver may not exist or was removed.
        </p>
        <Button variant="outline" onClick={() => navigate(ROUTES.DRIVERS)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Drivers
        </Button>
      </div>
    );
  }

  const userName = driver.user?.name || "Unknown";
  const userPhone = driver.user?.phone || "Unknown";
  const userEmail = driver.user?.email || "";

  const isPending = driver.verification_status === "pending";
  const isApproved = driver.verification_status === "approved";
  const isSuspended = driver.user?.is_active === false;

  const stats = driver.statistics;

  const documents = [
    { title: "Profile Photo", url: driver.profile_image },
    { title: "Aadhaar Front", url: driver.aadhar_front_image },
    { title: "Aadhaar Back", url: driver.aadhar_back_image },
    { title: "Driving License", url: driver.driving_license_image },
    { title: "Vehicle RC", url: driver.vehicle_rc_image },
  ];

  const handleApprove = () => approveMutation.mutate(driver.id);

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) return;
    rejectMutation.mutate(
      { id: driver.id, reason: rejectReason },
      { onSuccess: () => { setRejectModal(false); setRejectReason(""); } },
    );
  };

  const handleSuspendSubmit = () => {
    suspendMutation.mutate(
      { id: driver.id, reason: suspendReason || "Suspended by admin" },
      { onSuccess: () => { setSuspendModal(false); setSuspendReason(""); } },
    );
  };

  const handleActivate = () => activateMutation.mutate(driver.id);

  const handleResetSubmit = () => {
    if (resetConfirmText !== "RESET") return;
    resetMutation.mutate(driver.id, {
      onSuccess: () => {
        setResetModal(false);
        setResetConfirmText("");
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Driver Details"
        description={`Viewing profile for ${userName}`}
        breadcrumbs={[
          { label: "Drivers", href: ROUTES.DRIVERS },
          { label: userName },
        ]}
        actions={
          <Button variant="outline" onClick={() => navigate(ROUTES.DRIVERS)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      />

      {/* Header Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-background shadow-lg bg-primary/20 flex items-center justify-center">
                {driver.profile_image ? (
                  <img
                    src={driver.profile_image}
                    alt={userName}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <User className="h-9 w-9 text-primary" />
                )}
              </div>
              {driver.availability_status === "online" && (
                <span className="absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full bg-success border-2 border-background" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold">{userName}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">{userPhone}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <VerificationBadge status={driver.verification_status} />
                <AvailabilityBadge status={driver.availability_status} />
                {isSuspended && <Badge variant="destructive">Suspended</Badge>}
                {driver.vehicle_id ? (
                  <Badge variant="outline">Vehicle Assigned</Badge>
                ) : (
                  <Badge variant="secondary">No Vehicle</Badge>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {isPending && (
                <>
                  <Button
                    size="sm"
                    onClick={handleApprove}
                    disabled={approveMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setRejectModal(true)}
                  >
                    <XCircle className="h-4 w-4 mr-1.5" />
                    Reject
                  </Button>
                </>
              )}
              {isApproved && !isSuspended && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setSuspendModal(true)}
                >
                  <ShieldOff className="h-4 w-4 mr-1.5" />
                  Suspend
                </Button>
              )}
              {isSuspended && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleActivate}
                  disabled={activateMutation.isPending}
                >
                  <ShieldCheck className="h-4 w-4 mr-1.5" />
                  Activate
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Package}
          label="Total Trips"
          value={(stats?.total_bookings ?? driver.total_trips ?? 0).toLocaleString()}
        />
        <StatCard
          icon={CheckCircle}
          label="Completed"
          value={(stats?.completed_bookings ?? 0).toLocaleString()}
          color="success"
        />
        <StatCard
          icon={TrendingUp}
          label="Total Earnings"
          value={formatCurrency(stats?.total_earnings ?? 0)}
          color="primary"
        />
        <StatCard
          icon={Wallet}
          label="Wallet Balance"
          value={formatCurrency(Number(driver.wallet_balance || 0))}
          color="warning"
        />
      </div>

      {/* Live Location */}
      <DriverLocationMap
        lat={driver.current_lat}
        lng={driver.current_lng}
        status={driver.availability_status}
        name={userName}
        lastLocationAt={driver.last_location_at}
        lastActiveAddress={driver.last_active_address}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Personal Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-5 w-5 text-primary" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <InfoRow icon={User} label="Full Name" value={userName} />
            <InfoRow icon={Phone} label="Phone" value={userPhone} />
            <InfoRow icon={Mail} label="Email" value={userEmail || "Not provided"} />
            <InfoRow icon={MapPin} label="Address" value={driver.address || "Not provided"} />
            <InfoRow icon={Calendar} label="Joined" value={formatDate(driver.created_at)} />
            <InfoRow
              icon={Radio}
              label="Last Active"
              value={
                driver.last_location_at
                  ? `${formatLastActive(driver.last_location_at)} · ${new Date(driver.last_location_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}`
                  : "No GPS activity recorded"
              }
            />
            {driver.last_active_address && (
              <InfoRow icon={MapPin} label="Last Active Location" value={driver.last_active_address} />
            )}
          </CardContent>
        </Card>

        {/* License & Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-5 w-5 text-primary" />
              License & Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <InfoRow icon={FileText} label="License Number" value={driver.license_number || "Not provided"} />
            <InfoRow
              icon={Calendar}
              label="License Expiry"
              value={driver.license_expiry ? formatDate(driver.license_expiry) : "Not provided"}
            />
            <InfoRow
              icon={Star}
              label="Average Rating"
              value={driver.average_rating ? `${Number(driver.average_rating).toFixed(1)} / 5.0` : "No ratings yet"}
            />
            <InfoRow
              icon={CreditCard}
              label="Wallet Balance"
              value={formatCurrency(Number(driver.wallet_balance || 0))}
            />
          </CardContent>
        </Card>

        {/* Vehicle Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-5 w-5 text-primary" />
              Vehicle Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {driver.vehicle ? (
              <>
                <InfoRow
                  icon={Car}
                  label="Type"
                  value={driver.vehicle.vehicle_type ? String(driver.vehicle.vehicle_type).replace(/_/g, " ") : "—"}
                />
                <InfoRow
                  icon={FileText}
                  label="Registration No."
                  value={driver.vehicle.registration_number || "—"}
                />
                <InfoRow
                  icon={Car}
                  label="Model"
                  value={driver.vehicle.vehicle_model || "—"}
                />
                <InfoRow
                  icon={Activity}
                  label="Capacity"
                  value={vehicleCapacityLabel(driver.vehicle.vehicle_type)}
                />
                <InfoRow
                  icon={CheckCircle}
                  label="Vehicle Status"
                  value={driver.vehicle.verification_status ? String(driver.vehicle.verification_status) : "—"}
                />
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-2">No vehicle registered yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KYC Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary" />
            KYC Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {documents.map((doc) => (
              <DocumentCard
                key={doc.title}
                title={doc.title}
                imageUrl={doc.url}
                onPreview={() => doc.url && setImagePreview(doc.url)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bank Accounts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-5 w-5 text-primary" />
            Bank Accounts
            {driver.bank_accounts && driver.bank_accounts.length > 0 && (
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {driver.bank_accounts.length} account{driver.bank_accounts.length !== 1 ? "s" : ""}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {!driver.bank_accounts || driver.bank_accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
                <Building2 className="h-6 w-6 text-muted-foreground/50" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">No bank accounts added</p>
              <p className="text-xs text-muted-foreground mt-1">Driver has not added any bank account yet.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {driver.bank_accounts.map((account: DriverBankAccount) => (
                <BankAccountCard key={account.id} account={account} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ratings & Reviews */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Star className="h-5 w-5 text-primary" />
            Ratings & Reviews
            {driver.statistics && (
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                Avg {driver.average_rating ? Number(driver.average_rating).toFixed(1) : "—"} / 5.0
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {!driver.recent_ratings || driver.recent_ratings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
                <MessageSquare className="h-6 w-6 text-muted-foreground/50" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">No reviews yet</p>
              <p className="text-xs text-muted-foreground mt-1">Customer reviews will appear here after completed trips.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {driver.recent_ratings.map((r) => (
                <div key={r.id} className="flex items-start gap-3 p-3.5 rounded-xl border border-border/60 bg-card hover:bg-accent/20 transition-colors">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center font-bold text-sm text-primary">
                    {r.customer_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-sm font-semibold">{r.customer_name}</p>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map((star) => (
                          <Star
                            key={star}
                            className={`h-3.5 w-3.5 ${star <= r.rating ? "text-warning fill-warning" : "text-muted-foreground/30"}`}
                          />
                        ))}
                        <span className="text-xs font-semibold text-muted-foreground ml-1">{r.rating.toFixed(1)}</span>
                      </div>
                    </div>
                    {r.comment && (
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.comment}</p>
                    )}
                    <p className="text-xs text-muted-foreground/60 mt-1.5">
                      {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-destructive">
            <Trash2 className="h-5 w-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl border border-destructive/20 bg-destructive/5">
            <div>
              <p className="text-sm font-semibold">Reset Driver Data</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Permanently deletes all earnings, wallet balance, trips, bookings, ratings, and transaction history for this driver. This action cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              className="flex-shrink-0"
              onClick={() => setResetModal(true)}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Reset Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Image Preview Modal */}
      <Modal isOpen={!!imagePreview} onClose={() => setImagePreview(null)}>
        <ModalHeader onClose={() => setImagePreview(null)}>
          <ModalTitle>Document Preview</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Document"
              className="w-full rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "https://placehold.co/600x400/1e293b/94a3b8?text=Image+Not+Available";
              }}
            />
          )}
        </ModalBody>
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)}>
        <ModalHeader onClose={() => setRejectModal(false)}>
          <ModalTitle>Reject Driver</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Provide a reason for rejecting <strong>{userName}</strong>. This will be sent to the driver.
            </p>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Input
                placeholder="e.g. Documents unclear, invalid license..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setRejectModal(false)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleRejectSubmit}
            disabled={!rejectReason.trim() || rejectMutation.isPending}
          >
            {rejectMutation.isPending ? "Rejecting..." : "Confirm Reject"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Suspend Modal */}
      <Modal isOpen={suspendModal} onClose={() => setSuspendModal(false)}>
        <ModalHeader onClose={() => setSuspendModal(false)}>
          <ModalTitle>Suspend Driver</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Suspending <strong>{userName}</strong> will prevent them from receiving bookings.
            </p>
            <div className="space-y-1.5">
              <Label>Reason (optional)</Label>
              <Input
                placeholder="e.g. Policy violation, complaints received..."
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setSuspendModal(false)}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleSuspendSubmit}
            disabled={suspendMutation.isPending}
          >
            {suspendMutation.isPending ? "Suspending..." : "Confirm Suspend"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reset Data Modal */}
      <Modal isOpen={resetModal} onClose={() => { setResetModal(false); setResetConfirmText(""); }}>
        <ModalHeader onClose={() => { setResetModal(false); setResetConfirmText(""); }}>
          <ModalTitle>Reset Driver Data</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-destructive">This action is irreversible.</p>
                <p className="text-muted-foreground mt-1">
                  All of the following will be permanently deleted for <strong>{userName}</strong>:
                </p>
                <ul className="mt-2 space-y-0.5 text-muted-foreground list-disc list-inside">
                  <li>Wallet balance &amp; all transactions</li>
                  <li>All bookings &amp; payment records</li>
                  <li>Trip count &amp; total earnings</li>
                  <li>Ratings &amp; reviews received</li>
                  <li>Topup &amp; withdrawal history</li>
                </ul>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Type <strong>RESET</strong> to confirm</Label>
              <Input
                placeholder="RESET"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                className={resetConfirmText && resetConfirmText !== "RESET" ? "border-destructive" : ""}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => { setResetModal(false); setResetConfirmText(""); }}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleResetSubmit}
            disabled={resetConfirmText !== "RESET" || resetMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            {resetMutation.isPending ? "Resetting..." : "Reset All Data"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
